import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { drivers, type InviteBody } from "@/api/client";
import { CsvUpload } from "@/components/CsvUpload";
import { Field } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { useStagedImage } from "@/lib/hooks/useStagedImage";
import { CameraCapture, CSV_TEMPLATE, fullMsisdn, parseCsv, PhoneInput } from "@/modules/drivers/common";

export function NewDriverPage() {
  const qc = useQueryClient();
  const fb = useMutationFeedback();

  const [name, setName] = useState("");
  const [cc, setCc] = useState("+91");
  const [num, setNum] = useState("");
  const [curA, setCurA] = useState("");
  const [curP, setCurP] = useState("");
  const [sameAs, setSameAs] = useState(false);
  const [permA, setPermA] = useState("");
  const [permP, setPermP] = useState("");
  const [dl, setDl] = useState("");
  const staged = useStagedImage();          // revokes its object URL (was leaking one blob per keystroke)
  const photo = staged.blob;
  const [done, setDone] = useState("");
  const [bulkResult, setBulkResult] = useState("");

  const refresh = () => { void qc.invalidateQueries({ queryKey: ["drivers"] }); };
  const invite = useMutation({
    mutationFn: drivers.invite,
    onSuccess: (d) => {
      // photo rides along after the identity exists (it needs the driver_id);
      // a failed upload never fails the invite — retake on Edit Driver
      if (photo) {
        void drivers.uploadPhoto(d.driver_id, photo).catch(() => undefined);
      }
      fb.ok(); setDone(`${d.code} · ${d.name} invited${photo ? " (photo attached)" : ""}`);
      setName(""); setNum(""); setCurA(""); setCurP("");
      setPermA(""); setPermP(""); setDl(""); setSameAs(false); staged.set(null); refresh();
    },
    onError: (e) => { setDone(""); fb.fail(e); },
  });
  const bulk = useMutation({
    mutationFn: (rows: InviteBody[]) => drivers.bulk(rows),
    onSuccess: (r) => {
      fb.ok();
      setBulkResult(`${r.invited.length} invited` +
        (r.failed.length ? ` · ${r.failed.length} failed (${r.failed.map((f) => `${f.msisdn}: ${f.reason}`).join("; ")})` : ""));
      refresh();
    },
    onError: fb.fail,
  });

  return (
    <div className="mpage on">
      <PageHead eyebrow="Drivers · New Driver" title="Add drivers"
        desc="Invite-first: the driver appears as INVITED and turns ACTIVE when they verify their number on the Driver app. Only name + number are required — KYC can be completed later on Edit Driver." />

      <div className="section-label">Invite a driver</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
          <Field label="Full name" req>
            <SearchInput placeholder="Ravi Kumar" value={name} style={{ width: "100%" }}
              onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Phone number" req>
            <PhoneInput cc={cc} num={num} onCc={setCc} onNum={setNum} />
          </Field>
          <Field label="Driving licence no.">
            <SearchInput placeholder="MH12 20220012345" mono value={dl} style={{ width: "100%" }}
              onChange={(e) => setDl(e.target.value)} />
          </Field>
        </div>
        {/* minmax(0,…) + minWidth:0 — .search has min-width:210px, which a
            narrow grid column must be allowed to override (grid blowout) */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,3fr) minmax(0,1fr)", gap: 14, marginTop: 14 }}>
          <Field label="Current address">
            <SearchInput className="fill" placeholder="12, MG Road, Thane" value={curA}
              onChange={(e) => setCurA(e.target.value)} />
          </Field>
          <Field label="PIN">
            <SearchInput className="fill" mono placeholder="400601" value={curP}
              onChange={(e) => setCurP(e.target.value)} />
          </Field>
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5,
          color: "var(--t-mute)", marginTop: 10, cursor: "pointer", userSelect: "none" }}>
          <input type="checkbox" checked={sameAs} onChange={(e) => setSameAs(e.target.checked)} />
          Permanent address same as current
        </label>
        {!sameAs && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,3fr) minmax(0,1fr)", gap: 14, marginTop: 10 }}>
            <Field label="Permanent address">
              <SearchInput className="fill" placeholder="Village X, Satara" value={permA}
                onChange={(e) => setPermA(e.target.value)} />
            </Field>
            <Field label="PIN">
              <SearchInput className="fill" mono placeholder="415001" value={permP}
                onChange={(e) => setPermP(e.target.value)} />
            </Field>
          </div>
        )}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".8px", textTransform: "uppercase", color: "var(--t-mute)", marginBottom: 6 }}>Photo</div>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            {staged.url && (
              <img src={staged.url} alt="driver"
                style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, border: "2px solid var(--teal)" }} />
            )}
            <div style={{ flex: 1, minWidth: 240 }}>
              <CameraCapture onPhoto={staged.set} />
              {photo && (
                <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={() => staged.set(null)}>
                  Discard photo
                </button>
              )}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
          {done && <span style={{ color: "var(--ok)", fontSize: 12 }}>✓ {done}</span>}
          {fb.errorNode}
          <button className="btn teal" disabled={!name.trim() || !num.trim() || invite.isPending}
            onClick={() => invite.mutate({
              name: name.trim(), msisdn: fullMsisdn(cc, num),
              dl_number: dl.trim() || null,
              temp_address: curA.trim() || null,
              temp_pincode: curP.trim() || null,
              perm_address: (sameAs ? curA : permA).trim() || null,
              perm_pincode: (sameAs ? curP : permP).trim() || null,
            })}>
            Invite driver
          </button>
        </div>
      </div>

      <div className="section-label">Bulk invite (CSV)</div>
      <div className="panel panel-pad">
        <CsvUpload<InviteBody>
          referenceNote={<>Columns: <span className="mono">name, msisdn, current_address, current_pin,
            permanent_address, permanent_pin, dl_number</span> — only name + msisdn required; leave the
            permanent columns empty to copy the current address; wrap addresses containing commas in
            double quotes. Numbers: bare 10-digit = +91; other countries use the full +code form.
            Photos can't ride in a CSV — capture them on Edit Driver when the driver comes in.</>}
          templateUrl={CSV_TEMPLATE}
          parse={parseCsv}
          onParsed={() => setBulkResult("")}
          onSubmit={(items) => bulk.mutate(items)}
          actionLabel={(n) => `Invite ${n}`}
          busy={bulk.isPending}
          error={bulkResult && <span style={{ fontSize: 12, color: "var(--t-mute)" }}>{bulkResult}</span>}
        />
      </div>
    </div>
  );
}
